import { DateTime } from "luxon";
import { dayWindow, type AirportConfig, type Conflict, type CounterFunction, type FlightLeg, type Stand, type StandClosure, type Turn } from "@yb/core";
import type { Board } from "./api";
import { addDays, counterFunctionLabel, label } from "./format";

/**
 * The plan as paper: what a day's board says, laid out as tables a ramp team, the
 * check-in supervisor or the bag room can read without the app. Everything here is
 * worked out from the same board the screen draws, so the printout cannot disagree with
 * it; the page that prints it is PlanPrint.
 */

/** The parts of a day's plan that can be printed, in the order they are printed. */
export const PLAN_PARTS = [
  { id: "stands", label: "Stands & gates" },
  { id: "counters", label: "Check-in counters" },
  { id: "belts", label: "Reclaim belts" },
  { id: "outbound", label: "Outbound belts" },
] as const;
export type PlanPart = (typeof PLAN_PARTS)[number]["id"];

/** At most a week at a time: a longer printout is not read, and each day is a request. */
export const MAX_EXPORT_DAYS = 7;

export interface PlanExportRequest {
  from: string;
  days: number;
  parts: PlanPart[];
  /** The sandbox the board was showing, so the printout is of the same plan. Null for Live. */
  sandbox: number | null;
}

/** The address of the printable plan, opened in its own tab. */
export function planPrintUrl(r: PlanExportRequest): string {
  const q = new URLSearchParams({ print: "plan", from: r.from, days: String(r.days), parts: r.parts.join(",") });
  if (r.sandbox != null) q.set("sandbox", String(r.sandbox));
  return `/?${q.toString()}`;
}

/** The request back from that address; anything missing or odd falls back to one day, every part. */
export function readPlanPrintUrl(search: string): PlanExportRequest | null {
  const q = new URLSearchParams(search);
  const from = q.get("from") ?? "";
  if (q.get("print") !== "plan" || !/^\d{4}-\d{2}-\d{2}$/.test(from)) return null;
  const days = Math.min(MAX_EXPORT_DAYS, Math.max(1, Math.round(Number(q.get("days")) || 1)));
  const asked = (q.get("parts") ?? "").split(",");
  const parts = PLAN_PARTS.map((p) => p.id).filter((id) => asked.includes(id));
  const sandbox = q.get("sandbox");
  return { from, days, parts: parts.length ? parts : PLAN_PARTS.map((p) => p.id), sandbox: sandbox && /^\d+$/.test(sandbox) ? Number(sandbox) : null };
}

/** The dates a request covers, in order. */
export function exportDates(from: string, days: number): string[] {
  return Array.from({ length: Math.min(MAX_EXPORT_DAYS, Math.max(1, days)) }, (_, i) => addDays(from, i));
}

/**
 * A time as the printout writes it: "14:05" on the day printed, "21:40 Mon 9" when it
 * falls on the day before or after, which is how an overnight stay reads on paper.
 */
export function timeOn(ms: number | null | undefined, tz: string, date: string): string {
  if (ms == null) return "—";
  const d = DateTime.fromMillis(ms, { zone: tz });
  return d.toISODate() === date ? d.toFormat("HH:mm") : d.toFormat("HH:mm ccc d");
}

/** A leg's time: the schedule, and the newer time beside it when it is 3 minutes or more away. */
export function legTime(leg: FlightLeg, tz: string, date: string): { scheduled: string; latest: string | null } {
  const scheduled = timeOn(leg.scheduled, tz, date);
  const newer = leg.actual ?? leg.estimated ?? null;
  if (newer == null || Math.abs(newer - leg.scheduled) < 3 * 60_000) return { scheduled, latest: null };
  return { scheduled, latest: `${leg.actual != null ? "actual" : "est"} ${timeOn(newer, tz, date)}` };
}

export interface StandRow {
  turnId: string;
  flights: string;
  arr: { flight: string; from: string | null; time: { scheduled: string; latest: string | null }; cancelled: boolean } | null;
  dep: { flight: string; to: string | null; time: { scheduled: string; latest: string | null }; cancelled: boolean } | null;
  onStand: string;
  aircraft: string;
  registration: string | null;
  notes: string[];
  /** A hard conflict involves this turn: marked on paper with a word, not only a colour. */
  hard: boolean;
}

export interface StandGroup {
  /** Null is the group of turns with no stand yet. */
  stand: Stand | null;
  label: string;
  gate: string | null;
  rows: StandRow[];
}

/** A turn's flight numbers; the middle of a towed visit carries no flight, only its own label. */
export function turnLabel(t: Turn): string {
  return t.arr || t.dep ? label(t) : (t.displayLabel ?? "Towed aircraft");
}

const conflictWord = (c: Conflict) => (c.severity === "hard" ? "Conflict" : "Check");

/** What a planner would say about a turn out loud: overnight, towed, held, locked, cancelled, in conflict. */
export function turnNotes(t: Turn, board: Board, conflicts: Conflict[]): string[] {
  const tz = board.airport.timezone;
  const at = (ms: number) => timeOn(ms, tz, board.date);
  const notes: string[] = [];
  if (t.ron) notes.push("Overnight (RON)");
  if (t.segment === "arrival") notes.push(`Towed off at ${at(t.end)}`);
  if (t.segment === "towed") notes.push(`Towed here${t.displayLabel ? `: ${t.displayLabel}` : ""}`);
  if (t.segment === "departure") notes.push(`Towed in at ${at(t.start)}`);
  if (t.mech) notes.push(`Maintenance hold until ${at(t.mech.until)}${t.mech.note ? `: ${t.mech.note}` : ""}`);
  if (board.plan.stands[t.id]?.locked) notes.push("Locked to this stand");
  if (t.arr?.status === "cancelled" || t.dep?.status === "cancelled") notes.push(t.arr?.status === "cancelled" && t.dep?.status === "cancelled" ? "Cancelled" : `${t.arr?.status === "cancelled" ? "Arrival" : "Departure"} cancelled`);
  if (t.arr?.status === "diverted") notes.push("Arrival diverted");
  if (t.deiceMin) notes.push(`De-icing: ${t.deiceMin} min held on stand`);
  for (const c of conflicts) notes.push(`${conflictWord(c)}: ${c.message}`);
  return notes;
}

/** One group per active stand in the board's order, each visit in time order, then the turns with no stand. */
export function standGroups(board: Board, config: AirportConfig): StandGroup[] {
  const tz = board.airport.timezone;
  const byTurn = new Map<string, Conflict[]>();
  for (const c of board.conflicts) for (const id of c.turnIds) byTurn.set(id, [...(byTurn.get(id) ?? []), c]);
  const row = (t: Turn): StandRow => {
    const conflicts = byTurn.get(t.id) ?? [];
    return {
      turnId: t.id,
      flights: turnLabel(t),
      arr: t.arr ? { flight: t.arr.flightNumber, from: t.arr.otherAirport, time: legTime(t.arr, tz, board.date), cancelled: t.arr.status === "cancelled" } : null,
      dep: t.dep ? { flight: t.dep.flightNumber, to: t.dep.otherAirport, time: legTime(t.dep, tz, board.date), cancelled: t.dep.status === "cancelled" } : null,
      onStand: `${timeOn(t.start, tz, board.date)}–${timeOn(t.end, tz, board.date)}`,
      aircraft: `${t.aircraftType ?? "?"}${t.aircraftTypeInferred ? "*" : ""}`,
      registration: t.registration,
      notes: turnNotes(t, board, conflicts),
      hard: conflicts.some((c) => c.severity === "hard"),
    };
  };
  const sorted = [...board.turns].sort((a, b) => a.start - b.start || turnLabel(a).localeCompare(turnLabel(b)));
  const standOf = (t: Turn) => board.plan.stands[t.id]?.standId ?? null;
  const stands = [...config.stands].sort((a, b) => a.order - b.order);
  const groups: StandGroup[] = stands
    .map((s) => ({ stand: s, label: s.name, gate: s.gate, rows: sorted.filter((t) => standOf(t) === s.id).map(row) }))
    // An inactive stand is printed only when something is on it, which is itself worth seeing.
    .filter((g, i) => stands[i]!.active || g.rows.length > 0);
  // A stand the plan names but the settings no longer have still gets its rows printed.
  const known = new Set(stands.map((s) => s.id));
  for (const id of new Set(sorted.map(standOf).filter((s): s is string => s != null && !known.has(s)))) {
    groups.push({ stand: null, label: id, gate: null, rows: sorted.filter((t) => standOf(t) === id).map(row) });
  }
  const none = sorted.filter((t) => standOf(t) == null);
  if (none.length) groups.push({ stand: null, label: "No stand yet", gate: null, rows: none.map(row) });
  return groups;
}

/** The day's figures for the line under the heading. */
export function standSummary(board: Board): { turns: number; arrivals: number; departures: number; overnight: number; noStand: number; hard: number; warnings: number } {
  return {
    turns: board.turns.length,
    arrivals: board.turns.filter((t) => t.arr && t.segment !== "departure" && t.segment !== "towed").length,
    departures: board.turns.filter((t) => t.dep && t.segment !== "arrival" && t.segment !== "towed").length,
    overnight: board.turns.filter((t) => t.ron).length,
    noStand: board.turns.filter((t) => !board.plan.stands[t.id]?.standId).length,
    hard: board.conflicts.filter((c) => c.severity === "hard").length,
    warnings: board.conflicts.filter((c) => c.severity === "warning").length,
  };
}

/** Stand closures that touch the day, earliest first. */
export function dayClosures(board: Board): StandClosure[] {
  const { from, to } = dayWindow(board.date, board.airport.timezone);
  return board.closures.filter((c) => c.from < to && c.to > from).sort((a, b) => a.from - b.from);
}

/** Tows planned for the day, in time order, as the tow crew would read them. */
export function towLines(board: Board): Array<{ at: string; flight: string; toStand: string; note: string | null }> {
  const tz = board.airport.timezone;
  const flightOf = (legId: string | null) => {
    if (!legId) return null;
    const t = board.turns.find((x) => x.arr?.id === legId || x.dep?.id === legId);
    return t ? (t.arr?.id === legId ? t.arr.flightNumber : t.dep!.flightNumber) : null;
  };
  return board.events
    .flatMap((e) => (e.kind === "tow" ? [e] : []))
    .sort((a, b) => a.at - b.at)
    .map((e) => ({ at: timeOn(e.at, tz, board.date), flight: [flightOf(e.arrLegId), flightOf(e.depLegId)].filter(Boolean).join(" › ") || "An aircraft", toStand: e.toStand, note: e.note ?? null }));
}

/**
 * Counters as a person writes them: consecutive numbers with the same use become a range,
 * "5 Priority · 6–8 Check-in", rather than a list of identifiers.
 */
export function counterText(ids: string[], roles: Record<string, CounterFunction> | undefined, config: AirportConfig, labels?: Record<string, string>): string {
  // A signed counter reads as its sign, "14 WS-USA-PRIORITY"; the rest by their use.
  const known = ids
    .map((id) => ({ id, n: config.counters.find((c) => c.id === id)?.number ?? null, role: labels?.[id] ?? (roles?.[id] ? counterFunctionLabel(roles[id]) : null) }))
    .sort((a, b) => (a.n ?? Infinity) - (b.n ?? Infinity));
  const runs: Array<{ first: string; last: string; n: number | null; role: string | null }> = [];
  for (const c of known) {
    const prev = runs[runs.length - 1];
    if (prev && c.n != null && prev.n != null && c.n === prev.n + 1 && c.role === prev.role) {
      prev.last = String(c.n);
      prev.n = c.n;
    } else runs.push({ first: c.n != null ? String(c.n) : c.id, last: c.n != null ? String(c.n) : c.id, n: c.n, role: c.role });
  }
  return runs.map((r) => `${r.first === r.last ? r.first : `${r.first}–${r.last}`}${r.role ? ` ${r.role}` : ""}`).join(" · ");
}

export interface ResourceRow {
  legId: string;
  flight: string;
  airline: string;
  other: string | null;
  time: { scheduled: string; latest: string | null };
  /** The counters or belt, in words; "Not assigned" when the plan has none. */
  where: string;
  open: string;
  /** When the counters or belt open, for ordering. */
  start: number;
  extra: string | null;
  /** Set by a planner rather than by the app. */
  pinned: boolean;
  cancelled: boolean;
}

/**
 * One row per flight for check-in, reclaim or outbound, in time order. By flight rather
 * than by counter or belt, because the person reading it is looking for their flight.
 */
export function resourceRows(board: Board, config: AirportConfig, kind: "counters" | "belts" | "outbound"): ResourceRow[] {
  const tz = board.airport.timezone;
  const legs = new Map<string, { leg: FlightLeg; airline: string }>();
  for (const t of board.turns) {
    if (t.arr) legs.set(t.arr.id, { leg: t.arr, airline: t.airline });
    if (t.dep) legs.set(t.dep.id, { leg: t.dep, airline: t.airline });
  }
  const rows: ResourceRow[] = [];
  const span = (from: number, to: number) => `${timeOn(from, tz, board.date)}–${timeOn(to, tz, board.date)}`;
  if (kind === "counters") {
    for (const ca of Object.values(board.plan.counters)) {
      const f = legs.get(ca.legId);
      if (!f) continue;
      rows.push({
        start: ca.from,
        legId: ca.legId,
        flight: f.leg.flightNumber,
        airline: f.airline,
        other: f.leg.otherAirport,
        time: legTime(f.leg, tz, board.date),
        where: ca.counters.length ? counterText(ca.counters, ca.roles, config, ca.labels) : "Not assigned",
        open: span(ca.from, ca.to),
        extra: f.leg.pax ? `${f.leg.pax} pax` : null,
        pinned: !!board.overrides.counters?.[ca.legId],
        cancelled: f.leg.status === "cancelled",
      });
    }
  } else {
    const source = kind === "belts" ? board.plan.belts : (board.plan.outbound ?? {});
    const pins = kind === "belts" ? board.overrides.belts : board.overrides.outbound;
    const names = new Map<string, string>([...config.belts, ...(config.outboundBelts ?? [])].map((b) => [b.id, b.name]));
    for (const b of Object.values(source)) {
      const f = legs.get(b.legId);
      if (!f) continue;
      rows.push({
        start: b.from,
        legId: b.legId,
        flight: f.leg.flightNumber,
        airline: f.airline,
        other: f.leg.otherAirport,
        time: legTime(f.leg, tz, board.date),
        where: b.beltId ? (names.get(b.beltId) ?? b.beltId) : "Not assigned",
        open: span(b.from, b.to),
        extra: f.leg.bags ? `${f.leg.bags} bags` : null,
        pinned: pins?.[b.legId] !== undefined,
        cancelled: f.leg.status === "cancelled",
      });
    }
  }
  return rows.sort((a, b) => a.start - b.start || a.flight.localeCompare(b.flight));
}

/** The words for the plan version printed on every page: which version, when and by whom it was made. */
export function versionText(board: Board): string {
  const v = board.version;
  const when = DateTime.fromISO(v.createdAt, { zone: board.airport.timezone });
  const at = when.isValid ? `, saved ${when.toFormat("d LLL HH:mm")}` : "";
  return `Plan v${v.id}${at}${v.createdBy ? ` by ${v.createdBy}` : ""}: ${v.summary}`;
}

/** The time a page was printed, in the airport's time, as "27 Oct 2026 14:05". */
export function printedAt(ms: number, tz: string): string {
  return DateTime.fromMillis(ms, { zone: tz }).toFormat("d LLL yyyy HH:mm");
}

/** A name for the saved file, which the browser takes from the page title. */
export function planFileTitle(iata: string, dates: string[]): string {
  return dates.length > 1 ? `${iata} plan ${dates[0]} to ${dates[dates.length - 1]}` : `${iata} plan ${dates[0] ?? ""}`.trim();
}
